import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  DokaButton,
  DokaSearchField,
  DokaIcon,
  DokaModalController,
  DokaSelect,
  DokaSelectOption,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { firstValueFrom, of } from 'rxjs';
import {
  IUserHierarchyRegionModel,
  IUserHierarchyCountryModel,
  IUserHierarchyBranchModel,
  IHierarchyUserModel,
  LocationsClient,
  UsersClient,
} from 'src/api/services/generated/api-clients';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { AssignUserModalComponent, AssignmentLevel } from './assign-user-modal/assign-user-modal.component';
import { DeleteModalComponent } from 'src/app/shared/components/modals/delete-modal/delete-modal.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';

@Component({
  selector: 'app-master-data-users',
  templateUrl: './master-data-users.component.html',
  imports: [I18NextEagerPipe, DokaButton, DokaSearchField, DokaIcon, DokaSelect, DokaSelectOption, SpinnerComponent],
})
export class MasterDataUsersComponent {
  private readonly usersClient = inject(UsersClient);
  private readonly locationsClient = inject(LocationsClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);
  private readonly userInfoService = inject(UserInfoService);

  protected readonly divisions = rxResource({
    stream: () => this.locationsClient.getDivisions(),
  });

  protected readonly selectedDivisionId = signal<string | null>(null);

  protected readonly effectiveDivisionId = computed(() => {
    const selected = this.selectedDivisionId();
    if (selected) return selected;
    return this.userInfoService.defaultDivisionId(this.divisions.value()?.data ?? []) ?? null;
  });

  protected readonly hierarchy = rxResource({
    params: () => ({ divisionId: this.effectiveDivisionId() }),
    stream: ({ params }) => (params.divisionId ? this.usersClient.getUserHierarchy(params.divisionId) : of(null)),
  });

  protected readonly divisionUsers = computed(() => this.hierarchy.value()?.data?.users ?? []);
  protected readonly divisionName = computed(() => this.hierarchy.value()?.data?.name ?? '');

  protected searchText = signal('');

  protected readonly filteredRegions = computed(() => {
    const search = this.searchText().toLowerCase();
    const regions = this.hierarchy.value()?.data?.regions ?? [];

    if (!search) {
      return regions;
    }

    return regions
      .filter((region) => this.regionMatchesSearch(region, search))
      .map((region) => ({
        ...region,
        users: region.name.toLowerCase().includes(search)
          ? region.users
          : region.users.filter((u: IHierarchyUserModel) => this.userMatchesSearch(u, search)),
        countries: region.countries
          .filter((country: IUserHierarchyCountryModel) => this.countryMatchesSearch(country, search))
          .map((country: IUserHierarchyCountryModel) => ({
            ...country,
            users: country.name.toLowerCase().includes(search)
              ? country.users
              : country.users.filter((u: IHierarchyUserModel) => this.userMatchesSearch(u, search)),
            branches: country.branches
              .filter((branch: IUserHierarchyBranchModel) => this.branchMatchesSearch(branch, search))
              .map((branch: IUserHierarchyBranchModel) => ({
                ...branch,
                users: branch.name.toLowerCase().includes(search)
                  ? branch.users
                  : branch.users.filter((u: IHierarchyUserModel) => this.userMatchesSearch(u, search)),
              })),
          })),
      }));
  });

  protected expandedRegions = signal<Set<string>>(new Set());
  protected expandedCountries = signal<Set<string>>(new Set());
  protected expandedBranches = signal<Set<string>>(new Set());
  // User lists of the division, regions and countries, keyed by `${level}-${locationId}`; collapsed by default
  protected expandedUserGroups = signal<Set<string>>(new Set());

  protected toggleRegion(regionId: string): void {
    this.expandedRegions.update((set) => {
      const next = new Set(set);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return next;
    });
  }

  protected toggleCountry(countryId: string): void {
    this.expandedCountries.update((set) => {
      const next = new Set(set);
      if (next.has(countryId)) {
        next.delete(countryId);
      } else {
        next.add(countryId);
      }
      return next;
    });
  }

  protected toggleBranch(branchId: string): void {
    this.expandedBranches.update((set) => {
      const next = new Set(set);
      if (next.has(branchId)) {
        next.delete(branchId);
      } else {
        next.add(branchId);
      }
      return next;
    });
  }

  protected toggleUserGroup(level: AssignmentLevel, locationId: string): void {
    const key = `${level}-${locationId}`;
    this.expandedUserGroups.update((set) => {
      const next = new Set(set);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  private expandUserGroup(level: AssignmentLevel, locationId: string): void {
    this.expandedUserGroups.update((set) => new Set(set).add(`${level}-${locationId}`));
  }

  protected isUserGroupExpanded(level: AssignmentLevel, locationId: string, users: IHierarchyUserModel[]): boolean {
    const search = this.searchText().toLowerCase();
    return (
      this.expandedUserGroups().has(`${level}-${locationId}`) ||
      (search !== '' && users.some((u) => this.userMatchesSearch(u, search)))
    );
  }

  protected isRegionExpanded(regionId: string): boolean {
    return this.expandedRegions().has(regionId) || this.searchText() !== '';
  }

  protected isCountryExpanded(countryId: string): boolean {
    return this.expandedCountries().has(countryId) || this.searchText() !== '';
  }

  protected isBranchExpanded(branchId: string): boolean {
    return this.expandedBranches().has(branchId) || this.searchText() !== '';
  }

  protected getUserIds(users: IHierarchyUserModel[]): string[] {
    return users.map((u) => u.id);
  }

  protected formatUserName(user: IHierarchyUserModel): string {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.length > 0 ? `${parts.join(' ')} (${user.email})` : user.email;
  }

  private regionMatchesSearch(region: IUserHierarchyRegionModel, search: string): boolean {
    return (
      region.name.toLowerCase().includes(search) ||
      region.users.some((u) => this.userMatchesSearch(u, search)) ||
      region.countries.some((c) => this.countryMatchesSearch(c, search))
    );
  }

  private countryMatchesSearch(country: IUserHierarchyCountryModel, search: string): boolean {
    return (
      country.name.toLowerCase().includes(search) ||
      country.users.some((u) => this.userMatchesSearch(u, search)) ||
      country.branches.some((b) => this.branchMatchesSearch(b, search))
    );
  }

  private branchMatchesSearch(branch: IUserHierarchyBranchModel, search: string): boolean {
    return branch.name.toLowerCase().includes(search) || branch.users.some((u) => this.userMatchesSearch(u, search));
  }

  private userMatchesSearch(user: IHierarchyUserModel, search: string): boolean {
    return (
      user.email.toLowerCase().includes(search) ||
      (user.firstName?.toLowerCase().includes(search) ?? false) ||
      (user.lastName?.toLowerCase().includes(search) ?? false)
    );
  }

  protected readonly isSyncingDistributionLists = signal(false);

  protected async syncDistributionLists(): Promise<void> {
    this.isSyncingDistributionLists.set(true);

    try {
      const result = await firstValueFrom(this.usersClient.syncDistributionLists());
      const message = this.i18n.t('MasterData.Users.SyncDistributionListsSuccess', {
        synced: result.syncedLists,
        added: result.addedAssignments,
        removed: result.removedAssignments,
      });
      const skipped =
        result.failedLists > 0
          ? ' ' + this.i18n.t('MasterData.Users.SyncDistributionListsSkipped', { failed: result.failedLists })
          : '';

      await this.toastService.showSuccess(message + skipped);
      this.hierarchy.reload();
    } catch (error) {
      console.error('Error syncing distribution lists:', error);
      await this.toastService.showError(this.i18n.t('MasterData.Users.SyncDistributionListsError'));
    } finally {
      this.isSyncingDistributionLists.set(false);
    }
  }

  protected onDivisionChange(divisionId: string): void {
    this.selectedDivisionId.set(divisionId);
    this.userInfoService.rememberDivision(divisionId);
  }

  protected async openAssignModal(
    level: AssignmentLevel,
    locationId: string,
    locationName: string,
    existingUserIds: string[],
  ): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: AssignUserModalComponent,
      cssClass: 'assign-user-modal',
      componentProps: {
        level: signal(level).asReadonly(),
        locationId: signal(locationId).asReadonly(),
        locationName: signal(locationName).asReadonly(),
        existingUserIds: signal(existingUserIds).asReadonly(),
      },
    });

    await modal.present();
    const { role } = await modal.onDidDismiss();
    if (role === 'assigned') {
      // Show the newly assigned user
      if (level !== 'branch') {
        this.expandUserGroup(level, locationId);
      }
      await this.toastService.showSuccess(this.i18n.t('MasterData.Users.AssignSuccess'));
      this.hierarchy.reload();
    }
  }

  protected async removeUser(level: AssignmentLevel, locationId: string, user: IHierarchyUserModel): Promise<void> {
    const isBranch = level === 'branch';
    const modalTitle = this.i18n.t('MasterData.Users.RemoveUser');
    const modalText = isBranch
      ? this.i18n.t('MasterData.Users.RemoveBranchUserWarning', { name: this.formatUserName(user) })
      : this.i18n.t('MasterData.Users.RemoveUserConfirm', { name: this.formatUserName(user) });

    const modal = await this.modalCtrl.create({
      component: DeleteModalComponent,
      componentProps: {
        modalTitle: signal(modalTitle).asReadonly(),
        text: signal(modalText).asReadonly(),
      },
    });

    await modal.present();
    const { role } = await modal.onDidDismiss();
    if (role !== 'delete') return;

    try {
      switch (level) {
        case 'division':
          await firstValueFrom(this.usersClient.removeUserFromDivision(user.id, locationId));
          break;
        case 'region':
          await firstValueFrom(this.usersClient.removeUserFromRegion(user.id, locationId));
          break;
        case 'country':
          await firstValueFrom(this.usersClient.removeUserFromCountry(user.id, locationId));
          break;
        case 'branch':
          await firstValueFrom(this.usersClient.removeUserFromBranch(user.id, locationId));
          break;
      }
      await this.toastService.showSuccess(this.i18n.t('MasterData.Users.RemoveSuccess'));
      this.hierarchy.reload();
    } catch (error) {
      console.error('Error removing user:', error);
    }
  }
}
